import { useCallback } from 'react';
import type { FailedApiResponse } from 'components/well-page-content/types';
import { ListDrawer } from 'components/list-drawer';
import { format } from 'lib/ui';
import request from 'lib/request';
import type { InterestWorkspaceResponse } from 'types/api-responses';
import { WorkspacesQueryResponse } from './types';
import { extractApiErrorMessage } from './utils';
import { WorkspaceTile } from './workspace-tile';

interface WorkspacesPanelProps {
  entityId: number;
  open: boolean;
  onClose: () => void;
}

export const WorkspacesPanel = ({
  entityId,
  open,
  onClose,
}: WorkspacesPanelProps) => {
  const loader = useCallback(
    async ({
      text,
      page,
      pageSize,
    }: {
      text: string;
      page: number;
      pageSize: number;
    }) => {
      const response = await request.post<
        WorkspacesQueryResponse,
        FailedApiResponse
      >(
        '/queries/interest_certification_workspaces',
        {
          query: {
            entity_id: entityId,
            search: text,
            page,
            page_size: pageSize,
          },
        },
        new AbortController()
      );

      if (response.type === 'success' && response.data) {
        return {
          results: response.data.results,
          totalCount: response.data.totalCount,
        };
      }

      throw new Error(
        extractApiErrorMessage(response, 'Failed to load workspaces')
      );
    },
    [entityId]
  );

  const handleTileClick = (workspace: InterestWorkspaceResponse) => {
    window.open(workspace.href, '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <ListDrawer<InterestWorkspaceResponse>
      open={open}
      onClose={onClose}
      direction="right"
      size="480px"
      resetOnOpen={false}
      title="Workspaces"
      subtitle={(total) =>
        `${format.number(total)} workspace${total === 1 ? '' : 's'}`
      }
      loader={loader}
      pageSize={25}
      renderItem={(workspace) => (
        <WorkspaceTile workspace={workspace} onClick={handleTileClick} />
      )}
      getItemKey={(workspace) => workspace.id}
      search={{
        placeholder: 'Search',
        label: 'Search workspaces',
        debounceMs: 400,
      }}
      emptyText="No workspaces yet."
      emptySearchText="No workspaces match your search."
      errorText="Couldn't load workspaces."
    />
  );
};
